<template>
  <div class="navbar-container" v-if="authLoaded">
    <app-event></app-event>
    <div class="navbar-logo">
      <img alt="Nuance logo" :src="currentLogoPath" class="navbar-image" @click="toggleAbout" />
    </div>
    <div class="navbar-links">
      <template v-if="!user">
        <button class="navbar-button navbar-button-base" @click="$emit('open-login')">log in</button>
        <button class="navbar-button1 navbar-button-base" @click="$emit('open-signup')">sign up</button>
        <span class="navbar-link" @click="toggleAbout">about</span>
      </template>
      <template v-else>
        <span class="navbar-link" @click="$emit('open-profile')">profile</span>
        <span class="navbar-separator logged-in">—</span>
        <span class="navbar-link" @click="toggleAbout">about</span>
      </template>
    </div>
  </div>
</template>

<script>
import AppEvent from "./event";
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';

export default {
  name: "Navbar",
  props: {
    logo_path: {
      type: String,
      default: '/playground_assets/logo.png',
    },
    showAbout: {
      type: Boolean,
      default: false
    },
    inProfile: {
      type: Boolean,
      default: false
    }
  },
  components: {
    AppEvent,
  },
  data() {
    return {
      user: null,
      authLoaded: false,
      screenWidth: window.innerWidth
    }
  },
  created() {
    onAuthStateChanged(auth, user => {
      this.user = user;
      this.authLoaded = true;
    });
    // Add window resize listener
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    // Remove window resize listener
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    toggleAbout () {
      if (!this.showAbout) {
        this.$emit("open-about");
      } else {
        this.$emit("close-about")
      }
    },
    handleResize() {
      this.screenWidth = window.innerWidth;
    }
  },
  computed: {
    currentLogoPath() {
      // Use white PNG logo on mobile to avoid SVG pixelation issues on iOS
      if (this.screenWidth <= 768) {
        return '/playground_assets/logo-white.png';
      }
      return this.logo_path;
    }
  }
};
</script>

<style scoped>
.navbar-container {
  top: 1;
  width: 100%;
  display: flex;
  z-index: 10000;
  align-items: center;
  padding-top: var(--dl-space-space-unit);
  padding-bottom: var(--dl-space-space-unit);
  justify-content: center;
  position: relative;
  background-color: #fff;
  /* Add blur effect for modal overlay */
  backdrop-filter: blur(8px);
}

/* Mobile styles */
@media (max-width: 768px) {
  .navbar-container {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background-color: rgba(0, 0, 0, 0.95);
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    padding: 0.5rem 1rem;
  }
  
  /* Use pseudo-element for backdrop-filter to isolate it from logo */
  .navbar-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    z-index: -1;
    pointer-events: none;
  }
  
  .navbar-logo {
    position: relative;
    width: 100px;
    /* Completely isolate logo from backdrop-filter to prevent pixelation on iOS */
    z-index: 10;
    isolation: isolate;
    /* Force a new stacking context */
    contain: layout style paint;
  }
  
  /* iOS-specific fix for logo pixelation - remove backdrop-filter and use solid background */
  @supports (-webkit-touch-callout: none) {
    .navbar-container {
      background-color: rgba(0, 0, 0, 0.98);
    }
    
    .navbar-container::before {
      display: none;
    }
    
    .navbar-logo {
      will-change: transform;
      -webkit-transform: translate3d(0, 0, 0);
      transform: translate3d(0, 0, 0);
      /* Force a new compositing layer completely separate from any filters */
      opacity: 1;
      mix-blend-mode: normal;
    }
    
    .navbar-image {
      /* Force crisp SVG rendering on iOS */
      image-rendering: auto;
      -webkit-font-smoothing: antialiased;
      /* Force high-quality rendering */
      -webkit-transform: translate3d(0, 0, 0) scale(1);
      transform: translate3d(0, 0, 0) scale(1);
      /* Ensure no filter effects */
      filter: none !important;
      -webkit-filter: none !important;
      /* Prevent any subpixel rendering issues */
      -webkit-font-smoothing: subpixel-antialiased;
      /* Force hardware acceleration */
      will-change: transform;
    }
  }
  
  .navbar-links {
    position: relative;
    right: auto;
    gap: 0.5rem;
  }
  
  .navbar-button,
  .navbar-button1 {
    font-size: 0.7rem;
    padding: 0.3rem 0.8rem;
    margin-left: 0.3rem;
  }
  
  .navbar-link {
    font-size: 0.7rem;
  }
}

.sticky {
  position: fixed;
}

.navbar-logo {
  width: 120px;
  position: absolute;
  cursor: pointer;
}
.navbar-image {
  width: 100%;
  height: 100%;
  /* Fix pixelation on iOS by forcing hardware acceleration and proper image rendering */
  will-change: transform;
  image-rendering: auto;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  /* Force high-quality rendering on iOS */
  -webkit-transform: translate3d(0, 0, 0);
  transform: translate3d(0, 0, 0);
  /* Prevent any filter effects */
  filter: none !important;
  -webkit-filter: none !important;
}
.navbar-links {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  position: absolute;
  right: 10px;
}
.navbar-link {
  font-family: Syne;
  font-size: .75rem;
  cursor: pointer;
  text-decoration: underline;
  color: #000;
  line-height: 1;
  padding: 0.2rem 0;
}
.navbar-separator {
  font-family: Syne;
  font-size: .75rem;
  color: #000;
  margin-left: 0.5rem;
  margin-right: 0.5rem;
  line-height: 1;
}

.navbar-separator.logged-in {
  margin-left: 0.15rem;
  margin-right: 0.15rem;
}

/* Shared base class for both buttons - ensures identical sizing */
.navbar-button-base {
  font-size: 0.75rem;
  font-family: Syne, sans-serif;
  font-weight: 400;
  padding: 0.1rem 0.8rem;
  cursor: pointer;
  border-radius: 0px;
  box-sizing: border-box;
  line-height: 1;
  min-height: 1.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.navbar-button {
  color: #ffffff;
  background-color: #000000;
  border: none;
}

.navbar-button1 {
  color: #000000;
  background-color: transparent;
  border: 1px solid #000;
}

.navbar-button[data-v-07602a60], .navbar-button1[data-v-07602a60] {
  cursor: pointer;
  }
.navbar-links[data-v-07602a60] {
  gap: 0;
  display: flex;
  align-items: center;
  }
.navbar-button[data-v-07602a60]:active, .navbar-button1[data-v-07602a60]:active {
  outline: none;
  box-shadow: none;
  }
@media (max-width: 767px) {
  .navbar-links {
    right: 10px;
    gap: 0.5rem;
  }
}
@media (max-width: 768px) {
  .navbar-link {
    display: none;
  }
  .navbar-separator {
    display: none;
  }
  .navbar-button,
  .navbar-button1 {
    display: none;
  }
  .navbar-container {
    background-color: #000;
    padding-top: 1.8rem;
    padding-bottom: 1.5rem;
  }
  .navbar-logo {
    width: 120px;
  }
}
</style>
